import {TransactionStatus, TrustStatus} from '@sollapay/enums';
import {cn, IconBox, PopoverChevron, Typography} from '@sollapay/ui';
import {
  AmountDisplay,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton
} from '@sollapay/ui/components';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {BalanceBreakdownPanel} from './BalanceBreakdownPanel';
import {useNavigateToTab} from '../hooks';
import {computeBalanceBreakdown} from '../utils';

import type {BalanceBreakdown} from '../utils';
import type {TrustAccountLedgerDTO} from '@sollapay/types';

export interface TrustAccountViewBalanceMetricProps {
  status: TrustStatus;
  balance?: TrustAccountLedgerDTO | null;
  updatedAt?: number;
  isLoading?: boolean;
}

const CARD_CLASS_NAME =
  'flex flex-col justify-center gap-3 rounded-xl border border-border bg-background-surface px-5 py-3';

/** Hover and open share one look: accent border and lavender ring, no fill or drop shadow. */
const TRIGGER_CLASS_NAME = cn(
  'cursor-pointer transition-[border-color,box-shadow] duration-150',
  'hover:border-brand-accent/40 hover:ring-3 hover:ring-background-selected',
  'data-popup-open:border-brand-accent/40 data-popup-open:ring-3 data-popup-open:ring-background-selected',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
);

const TILE_ACTIVE_CLASS_NAME = cn(
  'transition-colors duration-150',
  'group-hover/popover-trigger:border-brand-accent/20 group-hover/popover-trigger:bg-background-selected',
  'group-data-popup-open/popover-trigger:border-brand-accent/20 group-data-popup-open/popover-trigger:bg-background-selected'
);

const TILE_ICON_ACTIVE_CLASS_NAME = cn(
  'transition-colors duration-150',
  'group-hover/popover-trigger:text-brand-accent group-data-popup-open/popover-trigger:text-brand-accent'
);

const CHEVRON_CLASS_NAME = cn(
  'size-3.5 transition-[color,rotate] duration-[120ms,180ms] motion-reduce:transition-none',
  'group-hover/popover-trigger:text-fg group-data-popup-open/popover-trigger:text-fg'
);

export function TrustAccountViewBalanceMetric({
  status,
  balance,
  updatedAt,
  isLoading = false
}: TrustAccountViewBalanceMetricProps) {
  if (status !== TrustStatus.TRUST_ACTIVE) return null;
  if (balance == null) {
    return isLoading ? <TrustAccountViewBalanceMetricSkeleton /> : null;
  }

  const breakdown = computeBalanceBreakdown(balance);

  if (!breakdown.hasInflight) {
    return (
      <div data-slot="trust-account-view-balance-metric" className={CARD_CLASS_NAME}>
        <BalanceSummary breakdown={breakdown} />
      </div>
    );
  }

  return <InflightBalanceCard breakdown={breakdown} updatedAt={updatedAt} />;
}

export function TrustAccountViewBalanceMetricSkeleton() {
  return (
    <div data-slot="trust-account-view-balance-metric-skeleton" className={CARD_CLASS_NAME}>
      <div className="flex items-center justify-between gap-6">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-33" />
          <Skeleton className="h-7.5 w-29" />
        </div>
        <Skeleton className="size-10 shrink-0 rounded-md" />
      </div>
    </div>
  );
}

interface InflightBalanceCardProps {
  breakdown: BalanceBreakdown;
  updatedAt?: number;
}

/** The whole card is the popover trigger, so hover and open state cover all of it. */
function InflightBalanceCard({breakdown, updatedAt}: InflightBalanceCardProps) {
  const {t, i18n} = useTranslation('trustAccounts');
  const navigateToTab = useNavigateToTab();
  const [isOpen, setIsOpen] = useState(false);

  const handleViewAll = () => {
    setIsOpen(false);
    navigateToTab('transactions', {status: TransactionStatus.INFLIGHT});
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        nativeButton={false}
        aria-label={t('view.balanceMetric.breakdown.title')}
        render={
          <div
            data-slot="trust-account-view-balance-metric"
            className={cn(CARD_CLASS_NAME, TRIGGER_CLASS_NAME)}
          />
        }
      >
        <BalanceSummary breakdown={breakdown} />
        <InflightPreviewRow breakdown={breakdown} />
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-96 p-5" dir={i18n.dir()}>
        <BalanceBreakdownPanel
          breakdown={breakdown}
          updatedAt={updatedAt}
          onViewAll={handleViewAll}
        />
      </PopoverContent>
    </Popover>
  );
}

interface BalanceSummaryProps {
  breakdown: BalanceBreakdown;
}

function BalanceSummary({breakdown}: BalanceSummaryProps) {
  const {t} = useTranslation('trustAccounts');

  return (
    <div className="flex items-center justify-between gap-6">
      <div className="flex flex-col items-start gap-2">
        <Typography size="sm" weight="medium" color="tertiary" className="whitespace-nowrap">
          {t('view.balanceMetric.title')}
        </Typography>
        <Typography size="xl" weight="semibold" className="tabular-nums">
          {breakdown.confirmed.formatNIS({symbol: true})}
        </Typography>
      </div>
      <IconBox
        icon="wallet"
        variant="secondary"
        iconColor="secondary"
        className={TILE_ACTIVE_CLASS_NAME}
        iconClassName={TILE_ICON_ACTIVE_CLASS_NAME}
      />
    </div>
  );
}

interface InflightPreviewRowProps {
  breakdown: BalanceBreakdown;
}

function InflightPreviewRow({breakdown}: InflightPreviewRowProps) {
  const {t} = useTranslation('trustAccounts');
  const pendingIn = breakdown.incoming.amount;
  const pendingOut = breakdown.outgoing.total.amount;

  return (
    <div
      data-slot="inflight-preview-row"
      className="flex w-full items-center gap-3 border-t border-border-subtle pt-2.5"
    >
      <div className="flex items-center gap-1.5 text-fg-tertiary">
        <Icon name="clock" className="size-3.5" />
        <Typography size="xs" weight="medium" color="tertiary">
          {t('view.balanceMetric.inflight.label')}
        </Typography>
      </div>
      <div data-slot="inflight-amounts" className="flex items-center gap-2">
        {!pendingIn.isZero() && (
          <AmountDisplay
            amount={pendingIn.toString()}
            variant="credit"
            size="xs"
            className="tabular-nums"
          />
        )}
        {!pendingOut.isZero() && (
          <AmountDisplay
            amount={pendingOut.toString()}
            variant="debit"
            size="xs"
            className="text-text-warning tabular-nums"
          />
        )}
      </div>
      <PopoverChevron className={cn(CHEVRON_CLASS_NAME, 'ms-auto')} />
    </div>
  );
}
